import { React, useEffect, useState } from "react";
import { Modal } from "@mui/material";
import Swal from "sweetalert2";
import PlantillaScreen from "../../../helpers/PlantillaScreen";
import { LoaderScreen } from "../../../components/LoaderScreen";
import request from "../../../../Helpers/API";
import { SearchSelect } from "../../ConfigurarLeads/SearchSelect";
import { SimpleSelect } from "../../ConfigurarLeads/SimpleSelect";

const UNIDAD_OPCIONES = [
    { value: "M", label: "Minutos" },
    { value: "H", label: "Horas" },
    { value: "D", label: "Días" },
];

// La prioridad ya no la elige el usuario en el ticket: se define aquí,
// a nivel de catálogo, y se hereda automáticamente a cada actividad.
const PRIORIDAD_OPCIONES = [
    { value: "A", label: "Alta" },
    { value: "M", label: "Mediana" },
    { value: "B", label: "Baja" },
];

const ESTADO_OPCIONES = [
    { value: "true", label: "Activo" },
    { value: "false", label: "Inactivo" },
];

const camposIniciales = {
    idactividad: -999,
    idgrupoactividades: "",
    nombreactividad: "",
    sla: "",
    tiemposolucion: "",
    tiempo: "H",
    prioridad: "A",
    estado: true,
    usuario: 1,
};

// Recurrencia (tickets automáticos) -- solo aplica al editar una actividad
// que ya existe (necesita un idactividad real para guardarse). Se guarda
// aparte, con su propio endpoint (Actividad/GuardarRecurrencia), sin tocar
// el flujo normal de Actividad/Guardar.
const FRECUENCIA_OPCIONES = [
    { value: "M", label: "Mensual (día del mes)" },
    { value: "S", label: "Semanal (día de la semana)" },
];

const DIA_SEMANA_OPCIONES = [
    { value: "1", label: "Lunes" },
    { value: "2", label: "Martes" },
    { value: "3", label: "Miércoles" },
    { value: "4", label: "Jueves" },
    { value: "5", label: "Viernes" },
    { value: "6", label: "Sábado" },
    { value: "7", label: "Domingo" },
];

const recurrenciaInicial = {
    esRecurrente: false,
    idSalaRecurrente: "",
    idUsuarioRecurrente: "",
    frecuenciaRecurrente: "M",
    diaRecurrente: "",
};

// Modal único para Alta y Edición de Cat_Actividad — el propio SP
// (SP_Cat_Actividad_Guardar) resuelve INSERT/UPDATE según idactividad.
export const ActividadModal = ({ open, onClose, editId = null, grupos = [], onSaved }) => {
    const [loader, setLoader] = useState({
        mostrar: false,
        alerta: false,
        error: false,
        mensaje: "",
    });
    const [form, setForm] = useState(camposIniciales);
    const [recurrencia, setRecurrencia] = useState(recurrenciaInicial);
    const [salas, setSalas] = useState([]);
    const [empleados, setEmpleados] = useState([]);

    const esEdicion = !!editId;

    useEffect(() => {
        if (!open) return;

        if (!editId) {
            setForm(camposIniciales);
            setRecurrencia(recurrenciaInicial);
            return;
        }

        setLoader((prev) => ({ ...prev, mostrar: true }));
        request
            .get(`Actividad/Obtener/${editId}`)
            .then((result) => {
                setForm({ ...result, usuario: 1 });
                setLoader((prev) => ({ ...prev, mostrar: false }));
            })
            .catch(() => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                Swal.fire("ACTIVIDADES", "No se pudo cargar la actividad", "error");
            });

        request
            .get(`Actividad/ObtenerRecurrencia/${editId}`)
            .then((result) => {
                const data = result?.data ?? result;
                if (!data) {
                    setRecurrencia(recurrenciaInicial);
                    return;
                }
                setRecurrencia({
                    esRecurrente: !!data.esRecurrente,
                    idSalaRecurrente: data.idSalaRecurrente ?? "",
                    idUsuarioRecurrente: data.idUsuarioRecurrente ?? "",
                    frecuenciaRecurrente: data.frecuenciaRecurrente || "M",
                    diaRecurrente: data.diaRecurrente ?? "",
                });
            })
            .catch(() => {
                setRecurrencia(recurrenciaInicial);
            });
    }, [open, editId]);

    // Salas y empleados para los selects de recurrencia -- se cargan una
    // sola vez al abrir el modal (mismos endpoints que ya usa el alta de
    // tickets y CatSalaResponsablesScreen, no se agrega nada nuevo al back
    // para esto).
    useEffect(() => {
        if (!open) return;

        request
            .get("Sala/Listado")
            .then((result) => setSalas(result?.data ?? result ?? []))
            .catch(() => { });

        request
            .get("SalaResponsable/EmpleadosDropDown")
            .then((result) => setEmpleados(result || []))
            .catch(() => { });
    }, [open]);

    const validar = () => {
        if (!form.idgrupoactividades) {
            Swal.fire("ACTIVIDADES", "Debe seleccionar un grupo", "warning");
            return false;
        }
        if (!form.nombreactividad || !form.nombreactividad.trim()) {
            Swal.fire("ACTIVIDADES", "El nombre es obligatorio", "warning");
            return false;
        }
        if (!form.sla) {
            Swal.fire("ACTIVIDADES", "El SLA es obligatorio", "warning");
            return false;
        }
        if (!form.tiemposolucion) {
            Swal.fire("ACTIVIDADES", "El tiempo de solución es obligatorio", "warning");
            return false;
        }
        if (!form.prioridad) {
            Swal.fire("ACTIVIDADES", "Debe seleccionar una prioridad", "warning");
            return false;
        }
        if (
            esEdicion &&
            recurrencia.esRecurrente &&
            (!recurrencia.idSalaRecurrente ||
                !recurrencia.idUsuarioRecurrente ||
                !recurrencia.frecuenciaRecurrente ||
                !recurrencia.diaRecurrente)
        ) {
            Swal.fire(
                "ACTIVIDADES",
                "Para marcar la actividad como recurrente completa sala, usuario, frecuencia y día",
                "warning"
            );
            return false;
        }
        return true;
    };

    const handleGuardar = async () => {
        if (!validar()) return;
        setLoader((prev) => ({ ...prev, mostrar: true }));

        try {
            await request.post("Actividad/Guardar", form);

            // La recurrencia solo se puede guardar sobre una actividad que
            // ya existe -- en alta nueva se omite (el usuario configura la
            // recurrencia editando la actividad después de creada).
            if (esEdicion) {
                await request.post("Actividad/GuardarRecurrencia", {
                    idActividad: Number(editId),
                    esRecurrente: recurrencia.esRecurrente,
                    idSalaRecurrente: recurrencia.esRecurrente ? Number(recurrencia.idSalaRecurrente) : null,
                    idUsuarioRecurrente: recurrencia.esRecurrente ? Number(recurrencia.idUsuarioRecurrente) : null,
                    frecuenciaRecurrente: recurrencia.esRecurrente ? recurrencia.frecuenciaRecurrente : null,
                    diaRecurrente: recurrencia.esRecurrente ? Number(recurrencia.diaRecurrente) : null,
                });
            }

            setLoader((prev) => ({ ...prev, mostrar: false }));
            Swal.fire(
                "ACTIVIDADES",
                esEdicion ? "Actividad actualizada correctamente" : "Actividad guardada correctamente",
                "success"
            );
            onSaved && onSaved();
            handleCancelar();
        } catch {
            setLoader((prev) => ({ ...prev, mostrar: false }));
            Swal.fire("ERROR", "No se pudo guardar la actividad", "error");
        }
    };

    const handleCancelar = () => {
        setForm(camposIniciales);
        setRecurrencia(recurrenciaInicial);
        onClose();
    };

    return (
        <Modal open={open} onClose={handleCancelar}>
            <div className="actividad-modal-formulario">
                <div className="actividad-modal-plantilla">
                    <div className="plantilla-screen-filtro-container" id="actividad-modal">
                        <PlantillaScreen
                            titulo={esEdicion ? "EDITAR ACTIVIDAD" : "NUEVA ACTIVIDAD"}
                            className_main={1}
                            className="plantilla-screen-filtro-container-modal"
                        >
                            <div className="cerrar-modal-button" onClick={handleCancelar}>X</div>
                            <LoaderScreen loader={loader} />
                            <div className="actividad-modal-content">
                                <div className="actividad-modal-content-container scroll-4">
                                    <div className="lead-form-group">
                                        <label>Grupo de actividades</label>
                                        <SearchSelect
                                            options={grupos.map((g) => ({
                                                value: g.idgrupoactividades,
                                                label: g.nombregrupoactividades,
                                            }))}
                                            value={form.idgrupoactividades}
                                            onChange={(val) => setForm((prev) => ({ ...prev, idgrupoactividades: val }))}
                                            placeholder="Seleccione un grupo"
                                        />
                                    </div>
                                    <div className="lead-form-group">
                                        <label>Nombre de la actividad</label>
                                        <input
                                            type="text"
                                            value={form.nombreactividad}
                                            onChange={(e) => setForm((prev) => ({ ...prev, nombreactividad: e.target.value }))}
                                            maxLength={30}
                                            placeholder="Ej. Falla de impresora"
                                        />
                                    </div>
                                    <div className="lead-form-group">
                                        <label>Prioridad</label>
                                        <SimpleSelect
                                            options={PRIORIDAD_OPCIONES}
                                            value={form.prioridad}
                                            onChange={(val) => setForm((prev) => ({ ...prev, prioridad: val }))}
                                            placeholder="Seleccione la prioridad"
                                        />
                                    </div>
                                    <div className="lead-form-group">
                                        <label>SLA (tiempo de respuesta)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={form.sla}
                                            onChange={(e) => setForm((prev) => ({ ...prev, sla: e.target.value }))}
                                        />
                                    </div>
                                    <div className="lead-form-group">
                                        <label>Tiempo de solución</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={form.tiemposolucion}
                                            onChange={(e) => setForm((prev) => ({ ...prev, tiemposolucion: e.target.value }))}
                                        />
                                    </div>
                                    <div className="lead-form-group">
                                        <label>Unidad de tiempo</label>
                                        <SimpleSelect
                                            options={UNIDAD_OPCIONES}
                                            value={form.tiempo}
                                            onChange={(val) => setForm((prev) => ({ ...prev, tiempo: val }))}
                                            placeholder="Seleccione la unidad"
                                        />
                                    </div>
                                    <div className="lead-form-group">
                                        <label>Estado</label>
                                        <SimpleSelect
                                            options={ESTADO_OPCIONES}
                                            value={form.estado ? "true" : "false"}
                                            onChange={(val) => setForm((prev) => ({ ...prev, estado: val === "true" }))}
                                            placeholder="Seleccione el estado"
                                        />
                                    </div>

                                    {esEdicion && (
                                        <>
                                            <label className="csce-requerido" style={{ marginTop: 8 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={recurrencia.esRecurrente}
                                                    onChange={(e) =>
                                                        setRecurrencia((prev) => ({ ...prev, esRecurrente: e.target.checked }))
                                                    }
                                                />
                                                Crear tickets automáticos de esta actividad (recurrente)
                                            </label>

                                            {recurrencia.esRecurrente && (
                                                <>
                                                    <div className="lead-form-group">
                                                        <label>Sala donde se crea el ticket</label>
                                                        <SearchSelect
                                                            options={salas.map((s) => ({ value: s.idsala, label: s.nombresala }))}
                                                            value={recurrencia.idSalaRecurrente}
                                                            onChange={(val) =>
                                                                setRecurrencia((prev) => ({ ...prev, idSalaRecurrente: val }))
                                                            }
                                                            placeholder="Seleccione una sala"
                                                        />
                                                    </div>
                                                    <div className="lead-form-group">
                                                        <label>Usuario que levanta el ticket</label>
                                                        <SearchSelect
                                                            options={empleados.map((e) => ({
                                                                value: e.IDEmpleado,
                                                                label: `${e.Nombre} ${e.ApellidoPaterno} ${e.ApellidoMaterno}`,
                                                            }))}
                                                            value={recurrencia.idUsuarioRecurrente}
                                                            onChange={(val) =>
                                                                setRecurrencia((prev) => ({ ...prev, idUsuarioRecurrente: val }))
                                                            }
                                                            placeholder="Seleccione un usuario"
                                                        />
                                                    </div>
                                                    <div className="lead-form-group">
                                                        <label>Frecuencia</label>
                                                        <SimpleSelect
                                                            options={FRECUENCIA_OPCIONES}
                                                            value={recurrencia.frecuenciaRecurrente}
                                                            onChange={(val) =>
                                                                setRecurrencia((prev) => ({
                                                                    ...prev,
                                                                    frecuenciaRecurrente: val,
                                                                    diaRecurrente: "",
                                                                }))
                                                            }
                                                        />
                                                    </div>
                                                    <div className="lead-form-group">
                                                        <label>
                                                            {recurrencia.frecuenciaRecurrente === "S"
                                                                ? "Día de la semana"
                                                                : "Día del mes (1-28)"}
                                                        </label>
                                                        {recurrencia.frecuenciaRecurrente === "S" ? (
                                                            <SimpleSelect
                                                                options={DIA_SEMANA_OPCIONES}
                                                                value={String(recurrencia.diaRecurrente)}
                                                                onChange={(val) =>
                                                                    setRecurrencia((prev) => ({ ...prev, diaRecurrente: val }))
                                                                }
                                                                placeholder="Seleccione un día"
                                                            />
                                                        ) : (
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                max="28"
                                                                value={recurrencia.diaRecurrente}
                                                                onChange={(e) =>
                                                                    setRecurrencia((prev) => ({ ...prev, diaRecurrente: e.target.value }))
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className="actividad-modal-buttons">
                                    <button
                                        className="actividad-modal-button-cancelar"
                                        onClick={handleCancelar}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        className="actividad-modal-button-guardar"
                                        onClick={handleGuardar}
                                        disabled={loader.mostrar}
                                    >
                                        Guardar
                                    </button>
                                </div>
                            </div>
                        </PlantillaScreen>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ActividadModal;
